import type { ColumnDef } from '@tanstack/react-table';
import type { KnowledgeIntentGroup } from '../types';
import { DataTableColumnHeader } from '@/components/ui/data-table';
import { Switch } from '@/components/ui/switch';
import { formatIntentLabel, intentServingStatus } from '../utils';

const NOT_PROMOTED_HINT = 'Promote its answers in Questions and answers';

interface KnowledgeIntentsColumnsOptions {
  canEdit: boolean;
  pendingIntent: string | null;
  onToggleEnabled: (group: KnowledgeIntentGroup, enabled: boolean) => void;
}

export function getKnowledgeIntentsColumns({
  canEdit,
  pendingIntent,
  onToggleEnabled
}: KnowledgeIntentsColumnsOptions): ColumnDef<KnowledgeIntentGroup>[] {
  return [
    {
      accessorKey: 'intent',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Intent' />,
      cell: ({ row }) => (
        <div className='flex min-w-0 max-w-[460px] flex-col'>
          <span className='truncate font-medium text-foreground' title={row.original.intent}>
            {formatIntentLabel(row.original.intent)}
          </span>
        </div>
      ),
      size: 460
    },
    {
      id: 'queries',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Queries' />,
      cell: ({ row }) => {
        const count = row.original.queries.length;
        return (
          <span className='text-sm text-muted-foreground'>
            <span className='font-medium text-foreground'>{count}</span> {count === 1 ? 'query' : 'queries'}
          </span>
        );
      },
      enableSorting: false,
      size: 140
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className='text-sm text-muted-foreground' data-testid='intent-status'>
          {intentServingStatus(row.original)}
        </span>
      ),
      enableSorting: false,
      size: 200
    },
    {
      id: 'enabled',
      header: 'Serving',
      cell: ({ row }) => {
        const group = row.original;
        // The switch pauses or resumes promoted answers; it cannot promote, so it is off
        // and locked while the intent has none.
        const notPromoted = group.promoted === 0;
        return (
          <div
            className='flex items-center'
            onClick={(e) => e.stopPropagation()}
            title={notPromoted ? NOT_PROMOTED_HINT : undefined}
          >
            <Switch
              className='cursor-pointer'
              aria-label={`Serving ${formatIntentLabel(group.intent)}`}
              checked={group.serving > 0}
              onCheckedChange={(checked) => onToggleEnabled(group, checked)}
              disabled={!canEdit || notPromoted || pendingIntent === group.intent}
            />
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 100,
      maxSize: 100
    }
  ];
}
